import { useEffect, useRef, useState } from 'react';

import IconChecks from '@/assets/Icons/IconChecks';
import IconX from '@/assets/Icons/IconX';

interface IInlineRenameInputProps {
  initialValue: string;
  maxLength: number;
  saveLabel: string;
  cancelLabel: string;
  ariaLabel: string;
  onSave: (value: string) => Promise<void>;
  onCancel: () => void;
  /** When true, select the input contents on mount. Default true. */
  autoSelect?: boolean;
}

/**
 * Shared inline rename input used for both folders and conversations (EWM-521 §5.2).
 * Validates on the trimmed value: empty or whitespace-only is rejected; max length applies.
 */
export const InlineRenameInput: React.FC<IInlineRenameInputProps> = ({
  initialValue,
  maxLength,
  saveLabel,
  cancelLabel,
  ariaLabel,
  onSave,
  onCancel,
  autoSelect = true,
}) => {
  const [draft, setDraft] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmed = draft.trim();
  const isValid = trimmed.length > 0 && trimmed.length <= maxLength;

  useEffect(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      if (autoSelect) {
        inputRef.current?.select();
      }
    });
  }, [autoSelect]);

  const handleSave = async () => {
    if (!isValid || isSaving) return;
    if (trimmed === initialValue.trim()) {
      onCancel();
      return;
    }
    setIsSaving(true);
    try {
      await onSave(trimmed);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className='flex items-center gap-1 py-1 pr-2'>
      <input
        ref={inputRef}
        type='text'
        value={draft}
        maxLength={maxLength}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void handleSave();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        disabled={isSaving}
        aria-invalid={!isValid}
        aria-label={ariaLabel}
        className={`flex-1 rounded border bg-(--background) px-2 py-1 text-sm focus:ring-1 focus:outline-hidden ${
          isValid
            ? 'border-(--border) focus:border-(--primary) focus:ring-(--primary)'
            : 'border-red-500 focus:border-red-500 focus:ring-red-500'
        }`}
      />
      <span
        className={`text-xs ${trimmed.length > maxLength * 0.9 ? 'text-amber-600' : 'text-gray-400'}`}
      >
        {draft.length} / {maxLength}
      </span>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void handleSave();
        }}
        disabled={!isValid || isSaving}
        className='rounded p-1 text-green-600 hover:bg-green-500/20 disabled:cursor-not-allowed disabled:opacity-40'
        title={saveLabel}
        type='button'
      >
        <IconChecks className='h-3.5 w-3.5' />
      </button>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onCancel();
        }}
        disabled={isSaving}
        className='rounded p-1 text-gray-500 hover:bg-gray-500/20'
        title={cancelLabel}
        type='button'
      >
        <IconX className='h-3.5 w-3.5' />
      </button>
    </div>
  );
};
